import React, { useEffect, useState } from "react";
import columns from "../../../columns/restoreClient/restoreClient";
import {
  GetDeletedClientApi,
  PermanentlyDeleteClientApi,
  RestoreClientApi,
} from "../../../api/request/restore/clients";
import SkeletonTable from "../../../components/table/SkeletonTable";
import moment from "moment";
import toast from "react-hot-toast";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import UserAvatar from "../../../components/common/UserAvatar";
import Search from "../../../components/common/Search";
import GlobalTooltip from "../../../components/common/GlobalTooltip";
import Delete from "../../../assets/Delete";

const RestoreClients = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteMode, setDeleteMode] = useState("single");

  const getDeletedClient = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetDeletedClientApi();
      if (data?.status) {
        setData(
          data?.data?.map((item) => {
            return {
              ...item,
              userDetails: (
                <UserAvatar
                  data={{
                    name: item?.name,
                    email: item?.email,
                    profile_image: item?.profile_image,
                  }}
                />
              ),
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "",
              deleted_at: item?.deleted_at
                ? moment(item?.deleted_at).format("lll")
                : "",
              action: (
                <div className="flex items-center gap-2">
                  <GlobalTooltip title={"Restore Client"}>
                    <button
                      onClick={() => {
                        setSelectedId(item?.id);
                        setIsOpen(true);
                      }}
                    >
                      <img src="/assets/icons/restore-file.svg" alt="Icon" />
                    </button>
                  </GlobalTooltip>
                  <GlobalTooltip title={"Delete Client"}>
                    <button
                      onClick={() => {
                        setSelectedRowKeys([item?.id]);
                        setDeleteMode("single");
                        setIsDeleteOpen(true);
                      }}
                    >
                      <Delete color="#000" size="5" />
                    </button>
                  </GlobalTooltip>
                </div>
              ),
            };
          })
        );
        setTotal(data?.total_record);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const restoreClient = async () => {
    try {
      setIsLoading(true);
      const params = { client_id: selectedId };
      const { data: response } = await RestoreClientApi(params);
      if (response?.status) {
        toast.success(response?.message);
        setIsOpen(false);
        setData(data?.filter((item) => item.id !== selectedId));
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const permanentlyDeleteClient = async () => {
    try {
      setIsLoading(true);
      const params = { id: selectedRowKeys };
      const { data: response } = await PermanentlyDeleteClientApi(params);
      if (response?.status) {
        toast?.success(response?.message);
        setData((prev) =>
          prev?.filter((item) => !selectedRowKeys?.includes(item.id))
        );
        setSelectedRowKeys([]);
        setIsDeleteOpen(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDeletedClient();
  }, []);

  useEffect(() => {
    setFiltered(data);
  }, [data]);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  return (
    <>
      <div className="border rounded-lg bg-White pb-5">
        <div className="sm:flex items-center justify-between p-5">
          <p className="title">
            Restore Clients {data?.length ? `(${data?.length})` : null}
          </p>
          <div className="md:w-auto w-40 ml-auto">
            <Search data={data} setFiltered={setFiltered} setTotal={setTotal} />
          </div>
        </div>

        {selectedRowKeys?.length > 0 && (
          <div className="flex justify-start px-5 mb-4">
            <button
              className="btn-primary !px-2 !py-1"
              onClick={() => {
                setDeleteMode("bulk");
                setIsDeleteOpen(true);
              }}
            >
              Delete Selected Clients
            </button>
          </div>
        )}

        <SkeletonTable
          rowKey="id"
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          pagination={{
            current: currentPage,
            total: total,
            pageSize: 10,
            showSizeChanger: false,
          }}
          handleTableChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
        />
      </div>

      {/* Confirmation modal for Restore Client */}
      <ConfirmationModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        message="Are you sure, you want to restore client?"
        onConfirm={restoreClient}
        isLoading={isLoading}
        loadingLabel="Restoring Client..."
      />

      {/* Confirmation modal for Permanently Delete CLient */}
      <ConfirmationModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        message={
          deleteMode === "single"
            ? "Are you sure you want to permanently delete this client?"
            : `Are you sure you want to permanently delete ${selectedRowKeys.length} clients?`
        }
        onConfirm={permanentlyDeleteClient}
        isLoading={isLoading}
        loadingLabel="Deleting Client..."
      />
    </>
  );
};

export default RestoreClients;
